import KanBoIcon from 'components/KanBoIcon'
import { ModalBoardContext, ModalCardContext } from 'components/Modal'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import { useCardContext } from 'dataProviders/Card'
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DocumentList from 'routes/Board/DocumentList'
import DocumentItem from 'routes/Board/DocumentList/DocumentItem'
import Element from '../Element'
import {
  MenuItem
} from 'components/Menu'
import { useBoardContext, useBoardPermission } from 'dataProviders/Board'
import { FormattedMessage } from 'react-intl'
import ElementItem from '../ElementItem'
import { dragType as documentDragType } from 'components/Document/Document'
import { NativeTypes } from 'react-dnd-html5-backend'
import { useDrop } from 'react-dnd'
import styled from 'styled-components'
import { findDOMNode } from 'react-dom'
import requestUploadStart from 'routes/Board/sagas/requestUploadStart'
import DocumentFolderSettings from './DocumentFolderSettings'
import AddDocumentButton from './AddDocumentButton'
import { KanBoLink } from 'components/Link'
import useMergedRef from '@react-hook/merged-ref'

const DraggableOver = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  z-index: 5;
  opacity: 0.5;
  pointer-events: none;
`

const DocumentComponent = (props) => <ElementItem component={DocumentItem} {...props} />

const TypedElement: any = Element

const DocumentFolder = React.forwardRef<typeof TypedElement & { refresh: () => void }, LeftSidebarProps & {
  id: number;
  sortable: any;
  elementRef: any,
}>(({
  leftSidebar,

  id,
  sortable,
  elementRef,
}, ref) => {
  const documentListRef = useRef<any>()

  const refreshDocuments = useCallback(() => {
    documentListRef.current?.refresh()
  }, [])

  useImperativeHandle(elementRef, () => ({
    refresh: refreshDocuments
  }))

  const dispatch = useDispatch()

  const cardContext = useCardContext()

  const boardContext = useBoardContext()

  const permission = useBoardPermission()

  const name = useSelector<any, number>(state => state['view-card'].elements.elementsById[id]?.name)

  const documentSourceId = useSelector<any, number>(state => state['view-card'].elements.elementsById[id]?.documentSourceId)

  const path = useSelector<any, string>(state => state['view-card'].elements.elementsById[id]?.path)

  const fullUrl = useSelector<any, string>(state => state['view-card'].elements.elementsById[id]?.fullUrl)

  const settings = useSelector<any, any>(state => state['view-card'].elements.elementsById[id]?.settings)

  const isBusy = useSelector<any, string>(state => state['view-card'].elements.elementsById[id]?.isBusy)

  const isCardArchived = useSelector<any, string>(state => state['view-board'].cards[cardContext.id].isArchived)

  const [documentListPath, setDocumentListPath] = useState<any[]>([])

  const currentPath = useMemo(() => {
    if (documentListPath.length > 0) {
      return documentListPath[documentListPath.length - 1]
    } else {
      return {
        documentSourceId,
        url: path,
      }
    }
  }, [documentListPath, documentSourceId, path])

  const handleSettingsClick = useCallback(() => {
    leftSidebar.push(() =>
      <ModalBoardContext boardId={boardContext.id}>
        <ModalCardContext cardId={cardContext.id}>
          <DocumentFolderSettings elementId={id} />
        </ModalCardContext>
      </ModalBoardContext>
    )
  }, [id, boardContext.id, cardContext.id])

  const handleGoToSourceClick = useCallback(() => {
    window.open(fullUrl, '_blank')
  }, [fullUrl])

  const [{ isDocumentOver }, drop] = useDrop({
    accept: [NativeTypes.FILE, documentDragType],
    collect: (monitor) => ({
      isDocumentOver: monitor.isOver({ shallow: true }),
    }),
    drop: (item: any) => {
      const { files } = item
  
      if (files) {
        var count = files.length
  
        if (count > 0) {
          for (var i = 0; i < count; i++) {
            const path = currentPath.url
  
            dispatch(requestUploadStart({
              file: files[i],
              documentSourceId,
              path,
              onUpload: () => {
                if (currentPath.url === path) {
                  refreshDocuments()
                }
              }
            }))
          }
        }
      }
    }
  })

  const menu = useMemo(() => {
    const menu = [
      <MenuItem
        key='go-to-source'
        icon={<KanBoIcon name='kanbo-16-go-to-source' />}
        onClick={handleGoToSourceClick}
      >
        <FormattedMessage id='DOCUMENTS_SOURCE_GO_TO' />
      </MenuItem>
    ]

    if (!isCardArchived && permission.member) {
      menu.push(
        <MenuItem
          key='settings'
          icon={<KanBoIcon name='kanbo-16-settings' />}
          onClick={handleSettingsClick}
        >
          <FormattedMessage id='SETTINGS' />
        </MenuItem>
      )
    }

    return menu
  }, [isCardArchived, permission.member, handleGoToSourceClick, handleSettingsClick])
  
  const mergedRef = useMergedRef(
    ref,
    ref => {
      const node = !isBusy ? (findDOMNode(ref) as Element | null) : null

      drop(node)
    },
  )

  return (
    <TypedElement
      ref={mergedRef}
      id={id}
      icon={<KanBoIcon name='kanbo-24-document-folder' />}
      collapsible
      sortable={sortable}
      menu={menu}
      nameInfo={
        <KanBoLink href={fullUrl}>{fullUrl}</KanBoLink>
      }
    >
      {(collapsed) =>
        <>
          {isDocumentOver && <DraggableOver />}
          {!collapsed ? (
            <DocumentList
              ref={documentListRef}
              title={name}
              showMax={settings?.ShowMaxItems}
              documentComponent={DocumentComponent}
              documentSourceId={documentSourceId}
              url={path}
              path={documentListPath}
              onPathChange={setDocumentListPath}
              defaultSort='name-asc'
            >
              {({ render, data }) =>
                <>
                  {render()}
                  {(data && !collapsed && !isCardArchived && permission.member) && (
                    <AddDocumentButton
                      documentFolderId={id}
                      url={currentPath?.url}
                      refreshDocuments={refreshDocuments}
                    />
                  )}
                </>
              }
            </DocumentList>
          ) : null}
        </>
      }
    </TypedElement>
  )
})

export default withLeftSidebar(DocumentFolder)
